import clsx from "clsx";
import React, { Children, useCallback, useRef, useState } from "react";
import { CurvedPill } from "web/react/components/filters-emo/curved-pill";
import SVGIcon from "web/react/components/svg-icon/svg-icon";
import { HStack } from "web/react/emo/h-stack";
import { Heading } from "web/react/emo/heading";
import { VStack } from "web/react/emo/v-stack";
import { View } from "web/react/emo/view";
import { useAboveFold } from "web/react/hooks/use-above-fold/use-above-fold";
import { useDomViewport } from "web/react/hooks/use-dom-viewport/use-dom-viewport";
import useIsomorphicLayoutEffect from "web/react/hooks/use-isomorphic-layout-effect/use-isomorphic-layout-effect";
import analytics from "web/script/analytics/analytics";
import { gettext } from "web/script/modules/django-i18n";
import environment from "web/script/modules/environment";
import * as styles from "./rail.css";

const DEFAULT_WRAP_LIMIT = Symbol("defaultWrapLimit");
const DESKTOP_WRAP_LIMIT = 10;
const MOBILE_WRAP_LIMIT = 5;

interface RelatedRailProps {
    analyticsLabel: string;
    children?: React.ReactNode[];
    title?: string;
    shouldWrap?: boolean;
    alwaysWrap?: boolean;
    wrapLimit?: number | typeof DEFAULT_WRAP_LIMIT;
    listItemClassName?: string;
}

export function Rail({
    title,
    children,
    analyticsLabel,
    listItemClassName,
    shouldWrap = false,
    alwaysWrap = false,
    wrapLimit = DEFAULT_WRAP_LIMIT,
}: RelatedRailProps): React.ReactElement {
    const [hasInteracted, setHasInteracted] = useState(false);
    const [seenAll, setSeenAll] = useState<boolean>(false);
    const [showMore, setShowMore] = useState<boolean>(false);
    const [isAtStart, setIsAtStart] = useState<boolean>(false);
    const [hasReachedEnd, setHasReachedEnd] = useState<boolean>(false);
    const [allItemsVisible, setAllItemsVisible] = useState<boolean>(false);

    const { isMobileViewport } = useDomViewport();

    const railID = `${analyticsLabel}-rail`;
    const railRef = useRef<HTMLOListElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const { isVisible } = useAboveFold(containerRef);

    const wrapLimitValue =
        wrapLimit === DEFAULT_WRAP_LIMIT
            ? isMobileViewport
                ? MOBILE_WRAP_LIMIT
                : DESKTOP_WRAP_LIMIT
            : wrapLimit;
    const wrap = (isMobileViewport && shouldWrap) || alwaysWrap;

    const isShowMoreButton = wrap && (children?.length ?? 0) > wrapLimitValue;

    const showScrollbar = environment.getFeature("beam_rail_scrollbar_variant");

    function isPillHidden(index: number): boolean {
        return index >= wrapLimitValue && !showMore;
    }

    const handleRailVisibilityAnalytics = useCallback(async (): Promise<void> => {
        const target = railRef?.current as HTMLOListElement;

        const noItemsHidden = !isShowMoreButton || (isShowMoreButton && showMore);
        setAllItemsVisible(target && target?.scrollWidth === target?.offsetWidth);
        setHasReachedEnd(
            target && target?.scrollLeft + target?.offsetWidth === target?.scrollWidth
        );
        setIsAtStart(target && target.scrollLeft === 0);

        if ((allItemsVisible || hasReachedEnd) && !seenAll && noItemsHidden) {
            analytics.event("rail", "seen last item", analyticsLabel, true);
            setSeenAll(true);
        }
    }, [analyticsLabel, isShowMoreButton, seenAll, showMore, allItemsVisible, hasReachedEnd]);

    const handleShowMoreClick = useCallback((): void => {
        handleRailVisibilityAnalytics();
        // only send event when we click to more (false -> true)
        !showMore && analytics.event("rail", "show more", analyticsLabel);
        setShowMore(!showMore);
    }, [showMore, analyticsLabel, handleRailVisibilityAnalytics]);

    function scrollInteraction(): void {
        if (!hasInteracted) {
            setHasInteracted(true);
            analytics.event("rail", "scroll", analyticsLabel);
        }
        handleRailVisibilityAnalytics();
    }

    function move(direction: string): void {
        const target = railRef?.current as HTMLOListElement;
        if (target) {
            const scrollFactor = direction === "left" ? -1 : 1;
            const scrollAmount = scrollFactor * (target.offsetWidth / 1.5);

            scrollInteraction();
            target.scrollLeft += scrollAmount;
        }
    }

    useIsomorphicLayoutEffect(() => {
        if (isVisible) {
            handleRailVisibilityAnalytics();
        }
    }, [isVisible, handleRailVisibilityAnalytics]);

    const isScrollable = !allItemsVisible && !wrap;

    return (
        <div ref={containerRef} data-testid={railID}>
            <VStack spacing="sm">
                <View marginBottom="md">
                    <HStack align="center" justify="space-between">
                        <Heading as={"h4"} textStyle={{ sm: "title-3", md: "title-2" }}>
                            {title}
                        </Heading>
                        {!showScrollbar && isScrollable && (
                            <HStack spacing="lg">
                                <button
                                    aria-label={gettext("general.previous_btn")}
                                    onClick={() => move("left")}
                                    data-testid="previous btn"
                                >
                                    <SVGIcon
                                        className={clsx(styles.button, styles.leftButton, {
                                            [styles.disabledButton]: isAtStart,
                                        })}
                                        name={"chevron"}
                                    />
                                </button>
                                <button
                                    aria-label={gettext("general.next_btn")}
                                    onClick={() => move("right")}
                                    data-testid="next btn"
                                >
                                    <SVGIcon
                                        className={clsx(styles.button, {
                                            [styles.disabledButton]: hasReachedEnd,
                                        })}
                                        name={"chevron"}
                                    />
                                </button>
                            </HStack>
                        )}
                    </HStack>
                </View>
                <ol
                    className={clsx(styles.rail, {
                        [styles.overflow]: !wrap,
                        [styles.scrollable]: showScrollbar && isScrollable,
                    })}
                    onScroll={scrollInteraction}
                    ref={railRef}
                >
                    <HStack spacing="xs" wrap={wrap}>
                        {Children.map(children, (child, index) => {
                            if (isPillHidden(index)) {
                                // Make list item available for SEO purposes
                                // when pill is hidden behind show more button
                                return (
                                    <li className={clsx(styles.seoLinks, listItemClassName)}>
                                        {child}
                                    </li>
                                );
                            } else {
                                return (
                                    <li
                                        className={clsx(styles.railItem, listItemClassName)}
                                        data-testid="rail-child"
                                    >
                                        {child}
                                    </li>
                                );
                            }
                        })}
                    </HStack>
                </ol>
                {isShowMoreButton && (
                    <div className={styles.showMoreButton}>
                        <CurvedPill
                            onClick={handleShowMoreClick}
                            variant={"tertiary"}
                            active={false}
                            activeHighlighted={false}
                            label={
                                showMore
                                    ? gettext("general.show_less")
                                    : gettext("general.show_more")
                            }
                        />
                    </div>
                )}
            </VStack>
        </div>
    );
}
